// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import styles from '@app/features/ui/action_menu/ContextMenu.module.css';
import * as ContextMenuCommands from '@app/features/ui/commands/ContextMenuCommands';
import {Scroller} from '@app/features/ui/components/Scroller';
import type {ContextMenu as ContextMenuType} from '@app/features/ui/state/ContextMenu';
import ContextMenuState from '@app/features/ui/state/ContextMenu';
import LayerManager from '@app/features/ui/state/LayerManager';
import {appZoomLayoutPx} from '@app/features/ui/utils/AppZoomUtils';
import {isMobileExperienceEnabled} from '@app/features/ui/utils/MobileExperience';
import {isScrollbarDragActive} from '@app/features/ui/utils/ScrollbarDragState';
import {canUseWindowFocusedHoverControls} from '@app/features/ui/utils/WindowFocusInteractionGuard';
import {wasPointerDownInside} from '@app/lib/overlay/dismissGuard';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import React, {useCallback, useContext, useEffect, useLayoutEffect, useRef, useState} from 'react';
import type {PressEvent} from 'react-aria-components';
import {
	Menu as AriaMenu,
	MenuItem as AriaMenuItem,
	MenuSection as AriaMenuSection,
	Popover as AriaPopover,
	Separator as AriaSeparator,
	SubmenuTrigger as AriaSubmenuTrigger,
} from 'react-aria-components';

import {createPortal, flushSync} from 'react-dom';

const ContextMenuCloseContext = React.createContext<() => void>(() => {});

export const useContextMenuClose = () => useContext(ContextMenuCloseContext);

export const ContextMenuCloseProvider = ContextMenuCloseContext.Provider;
function stopHoverEventWhenWindowCannotUseHover(event: React.SyntheticEvent): void {
	if (canUseWindowFocusedHoverControls()) return;
	event.stopPropagation();
}
export function useCheckedMenuItemRef(
	role: 'menuitemcheckbox' | 'menuitemradio',
	checked: boolean,
	forwardedRef: React.ForwardedRef<HTMLDivElement>,
): (node: HTMLDivElement | null) => void {
	const itemRef = useRef<HTMLDivElement | null>(null);
	const setItemRef = useCallback(
		(node: HTMLDivElement | null) => {
			itemRef.current = node;
			if (typeof forwardedRef === 'function') {
				forwardedRef(node);
			} else if (forwardedRef) {
				forwardedRef.current = node;
			}
		},
		[forwardedRef],
	);
	useLayoutEffect(() => {
		const item = itemRef.current;
		if (!item) return;
		item.setAttribute('role', role);
		item.setAttribute('aria-checked', checked ? 'true' : 'false');
	});
	return setItemRef;
}
const menuItemSelector = [
	'[role="menuitem"]:not([aria-disabled="true"])',
	'[role="menuitemcheckbox"]:not([aria-disabled="true"])',
	'[role="menuitemradio"]:not([aria-disabled="true"])',
].join(', ');
const textValueFromNode = (node: React.ReactNode): string => {
	if (typeof node === 'string') return node;
	if (typeof node === 'number') return String(node);
	if (Array.isArray(node)) return node.map(textValueFromNode).join('');
	if (React.isValidElement(node)) {
		return textValueFromNode((node.props as {children?: React.ReactNode}).children);
	}
	return '';
};
interface RootContextMenuProps {
	contextMenu: ContextMenuType;
}
const RootContextMenuInner: React.FC<RootContextMenuProps> = observer(({contextMenu}) => {
	const {t} = useLingui();
	const [isOpen, setIsOpen] = useState(true);
	const [isPositioned, setIsPositioned] = useState(false);
	const [position, setPosition] = useState({x: 0, y: 0});
	const shouldTrackDynamicPosition = Boolean(contextMenu.config?.trackDynamicPosition);
	const menuRef = useRef<HTMLDivElement>(null);
	const menuContentRef = useRef<HTMLDivElement>(null);
	const positionRafRef = useRef<number | null>(null);
	const settleRafRef = useRef<number | null>(null);
	const positioningObserverRef = useRef<ResizeObserver | null>(null);
	const isPositionedRef = useRef(false);
	const focusMenuItemAtBoundary = useCallback((boundary: 'first' | 'last') => {
		const menuElement = menuContentRef.current;
		if (!menuElement) {
			return false;
		}
		const interactableItems = menuElement.querySelectorAll<HTMLElement>(menuItemSelector);
		const menuItem = boundary === 'last' ? interactableItems[interactableItems.length - 1] : interactableItems[0];
		(menuItem ?? menuElement).focus({preventScroll: true});
		return true;
	}, []);
	const close = useCallback(() => {
		setIsOpen(false);
		ContextMenuCommands.close();
	}, []);
	const clearScheduledPositioning = useCallback(() => {
		if (positionRafRef.current !== null) {
			cancelAnimationFrame(positionRafRef.current);
			positionRafRef.current = null;
		}
		if (settleRafRef.current !== null) {
			cancelAnimationFrame(settleRafRef.current);
			settleRafRef.current = null;
		}
	}, []);
	const disconnectPositioningObserver = useCallback(() => {
		positioningObserverRef.current?.disconnect();
		positioningObserverRef.current = null;
	}, []);
	const updatePositionState = useCallback((nextPosition: {x: number; y: number}, sync = false) => {
		const applyUpdate = () => {
			setPosition((current) => (current.x === nextPosition.x && current.y === nextPosition.y ? current : nextPosition));
		};
		if (sync) {
			flushSync(applyUpdate);
			return;
		}
		applyUpdate();
	}, []);
	const setPositionedState = useCallback((nextPositioned: boolean) => {
		isPositionedRef.current = nextPositioned;
		setIsPositioned(nextPositioned);
	}, []);
	const updatePosition = useCallback(
		(options?: {sync?: boolean}) => {
			const {x, y} = contextMenu.target;
			const align = contextMenu.config?.align ?? 'top-left';
			const menuElement = menuRef.current;
			if (!menuElement) {
				return;
			}
			const rect = menuElement.getBoundingClientRect();
			const viewportWidth = window.innerWidth;
			const viewportHeight = window.innerHeight;
			const cursorOffset = 4;
			const edgePadding = 12;
			let finalX: number;
			let finalY: number;
			if (align === 'bottom-left') {
				finalX = x;
				finalY = y - rect.height - 12;
			} else if (align === 'bottom-right') {
				finalX = x - rect.width;
				finalY = y - rect.height;
				if (finalX < edgePadding) {
					finalX = Math.max(edgePadding, viewportWidth - rect.width - edgePadding);
				}
				if (finalY < edgePadding) {
					finalY = Math.max(edgePadding, viewportHeight - rect.height - edgePadding);
				}
			} else {
				finalY = y + cursorOffset;
				if (align === 'top-right') {
					finalX = x - rect.width;
					if (finalX < edgePadding) {
						finalX = x + cursorOffset;
						if (finalX + rect.width > viewportWidth - edgePadding) {
							finalX = Math.max(edgePadding, viewportWidth - rect.width - edgePadding);
						}
					}
				} else {
					finalX = x + cursorOffset;
					if (finalX + rect.width > viewportWidth - edgePadding) {
						finalX = x - rect.width - cursorOffset;
						if (finalX < edgePadding) {
							finalX = Math.max(edgePadding, viewportWidth - rect.width - edgePadding);
						}
					}
				}
			}
			if (align !== 'bottom-left' && align !== 'bottom-right') {
				if (finalY + rect.height > viewportHeight - edgePadding) {
					finalY = y - rect.height - cursorOffset;
					if (finalY < edgePadding) {
						finalY = Math.max(edgePadding, viewportHeight - rect.height - edgePadding);
					}
				}
			}
			finalX = Math.max(edgePadding, finalX);
			finalY = Math.max(edgePadding, finalY);
			updatePositionState({x: finalX, y: finalY}, options?.sync ?? false);
		},
		[contextMenu.config?.align, contextMenu.target, updatePositionState],
	);
	const scheduleInitialPositioning = useCallback(() => {
		if (!menuRef.current) {
			return;
		}
		clearScheduledPositioning();
		updatePosition();
		positionRafRef.current = requestAnimationFrame(() => {
			positionRafRef.current = null;
			updatePosition();
			settleRafRef.current = requestAnimationFrame(() => {
				settleRafRef.current = null;
				if (!shouldTrackDynamicPosition) {
					disconnectPositioningObserver();
				}
				setPositionedState(true);
			});
		});
	}, [
		clearScheduledPositioning,
		disconnectPositioningObserver,
		setPositionedState,
		shouldTrackDynamicPosition,
		updatePosition,
	]);
	useLayoutEffect(() => {
		const menuElement = menuRef.current;
		setPositionedState(false);
		scheduleInitialPositioning();
		if (!menuElement) {
			return () => {
				clearScheduledPositioning();
				disconnectPositioningObserver();
			};
		}
		if (typeof ResizeObserver !== 'undefined') {
			const observer = new ResizeObserver(() => {
				if (isPositionedRef.current && shouldTrackDynamicPosition) {
					updatePosition({sync: true});
					return;
				}
				scheduleInitialPositioning();
			});
			positioningObserverRef.current = observer;
			observer.observe(menuElement);
			if (menuContentRef.current) {
				observer.observe(menuContentRef.current);
			}
		}
		return () => {
			clearScheduledPositioning();
			disconnectPositioningObserver();
		};
	}, [
		clearScheduledPositioning,
		disconnectPositioningObserver,
		scheduleInitialPositioning,
		setPositionedState,
		shouldTrackDynamicPosition,
		updatePosition,
	]);
	useEffect(() => {
		const handleResize = () => {
			updatePosition({sync: true});
		};
		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, [updatePosition]);
	useEffect(() => {
		if (isOpen) {
			LayerManager.addLayer('contextmenu', contextMenu.id, close);
			return () => {
				LayerManager.removeLayer('contextmenu', contextMenu.id);
			};
		}
		return;
	}, [isOpen, contextMenu.id, close]);
	const handleBackdropClick = useCallback(() => {
		if (isScrollbarDragActive()) {
			return;
		}
		if (wasPointerDownInside(menuRef.current)) {
			return;
		}
		close();
	}, [close]);
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				close();
				return;
			}
			const menuElement = menuContentRef.current;
			if (!menuElement) {
				return;
			}
			const activeElement = document.activeElement;
			const isFocusInsideMenu =
				activeElement instanceof HTMLElement &&
				(menuElement.contains(activeElement) || Boolean(activeElement.closest(`.${styles.submenuPopover}`)));
			if (!isFocusInsideMenu) {
				if (e.key === 'ArrowDown' || e.key === 'Home') {
					if (focusMenuItemAtBoundary('first')) {
						e.preventDefault();
						e.stopPropagation();
					}
					return;
				}
				if (e.key === 'ArrowUp' || e.key === 'End') {
					if (focusMenuItemAtBoundary('last')) {
						e.preventDefault();
						e.stopPropagation();
					}
					return;
				}
			}
			const eventTarget = e.target;
			const isEditableTarget =
				eventTarget instanceof HTMLElement &&
				(eventTarget instanceof HTMLInputElement ||
					eventTarget instanceof HTMLTextAreaElement ||
					eventTarget.isContentEditable);
			if (isFocusInsideMenu && !isEditableTarget && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
				const menuItems = menuElement.querySelectorAll<HTMLElement>(menuItemSelector);
				const pressedKey = e.key.toLowerCase();
				for (const item of menuItems) {
					const shortcutElement =
						item.querySelector(`.${styles.itemShortcut}`) || item.querySelector(`.${styles.itemShortcutHidden}`);
					if (shortcutElement) {
						const shortcutText = shortcutElement.textContent?.toLowerCase().trim();
						if (shortcutText === pressedKey) {
							e.preventDefault();
							e.stopPropagation();
							item.focus({preventScroll: true});
							item.dispatchEvent(
								new KeyboardEvent('keydown', {key: 'Enter', code: 'Enter', bubbles: true, cancelable: true}),
							);
							item.dispatchEvent(
								new KeyboardEvent('keyup', {key: 'Enter', code: 'Enter', bubbles: true, cancelable: true}),
							);
							return;
						}
					}
				}
			}
		};
		const handleClickOutside = (e: MouseEvent) => {
			if (isScrollbarDragActive()) {
				return;
			}
			if (wasPointerDownInside(menuRef.current)) {
				return;
			}
			const targetElement = e.target as HTMLElement;
			if (menuRef.current && !menuRef.current.contains(targetElement)) {
				if (targetElement.closest?.(`.${styles.submenuPopover}`)) {
					return;
				}
				close();
			}
		};
		document.addEventListener('keydown', handleKeyDown);
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [close, focusMenuItemAtBoundary]);
	if (!isOpen) {
		return null;
	}
	const {x, y} = contextMenu.target;
	return (
		<div className={styles.contextMenuOverlay} data-overlay-pass-through="true">
			<div className={styles.backdrop} onClick={handleBackdropClick} aria-hidden="true" />
			<div
				ref={menuRef}
				className={styles.contextMenu}
				style={{
					position: 'fixed',
					left: `${appZoomLayoutPx(isPositioned ? position.x : x)}px`,
					top: `${appZoomLayoutPx(isPositioned ? position.y : y)}px`,
					opacity: isPositioned ? 1 : 0,
					visibility: isPositioned ? 'visible' : 'hidden',
					pointerEvents: isPositioned ? 'auto' : 'none',
					zIndex: 'var(--z-index-contextmenu)',
				}}
				onMouseMoveCapture={stopHoverEventWhenWindowCannotUseHover}
				onMouseOverCapture={stopHoverEventWhenWindowCannotUseHover}
				onPointerMoveCapture={stopHoverEventWhenWindowCannotUseHover}
				onPointerOverCapture={stopHoverEventWhenWindowCannotUseHover}
			>
				<ContextMenuCloseContext.Provider value={close}>
					<Scroller
						className={styles.menuScroller}
						overflow="auto"
						fade={false}
						key={`context-menu-scroller-${contextMenu.id}`}
					>
						<AriaMenu ref={menuContentRef} className={styles.ariaMenu} aria-label={t`Context menu`}>
							{contextMenu.render({onClose: close})}
						</AriaMenu>
					</Scroller>
				</ContextMenuCloseContext.Provider>
			</div>
		</div>
	);
});
export const RootContextMenu: React.FC<RootContextMenuProps> = observer(({contextMenu}) => {
	return <RootContextMenuInner contextMenu={contextMenu} />;
});
interface MenuItemProps {
	label: string;
	disabled?: boolean;
	onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
	onSelect?: (event: PressEvent) => void;
	icon?: React.ReactNode;
	danger?: boolean;
	color?: string;
	className?: string;
	children?: React.ReactNode;
	closeOnSelect?: boolean;
	shortcut?: React.ReactNode;
	hint?: string;
}
export const MenuItem = React.forwardRef<HTMLDivElement, MenuItemProps>(
	(
		{label, disabled, onSelect, icon, danger, className, children, closeOnSelect = true, shortcut, hint},
		forwardedRef,
	) => {
		const closeMenu = useContext(ContextMenuCloseContext);
		const shouldShowShortcuts = Accessibility.showContextMenuShortcuts;
		const hasShortcut = Boolean(shortcut);
		const shouldShowShortcut = hasShortcut && shouldShowShortcuts;
		const inFlightRef = useRef(false);
		const handlePress = useCallback(
			(event: PressEvent) => {
				if (disabled) return;
				if (inFlightRef.current) return;
				inFlightRef.current = true;
				try {
					onSelect?.(event);
				} finally {
					if (closeOnSelect) {
						closeMenu();
					}
					queueMicrotask(() => {
						inFlightRef.current = false;
					});
				}
			},
			[closeMenu, closeOnSelect, disabled, onSelect],
		);
		const labelContent = children ?? label;
		return (
			<AriaMenuItem
				ref={forwardedRef}
				onPress={handlePress}
				isDisabled={disabled}
				shouldCloseOnSelect={closeOnSelect}
				className={clsx(styles.item, className, {
					[styles.danger]: danger,
					[styles.disabled]: disabled,
				})}
				textValue={label || textValueFromNode(children)}
			>
				<div className={hint ? styles.itemLabelContainer : styles.itemLabel}>
					{hint ? (
						<>
							<div className={styles.itemLabelText}>{labelContent}</div>
							<div className={styles.itemHint}>{hint}</div>
						</>
					) : (
						<>
							{labelContent}
							{hasShortcut && (
								<span
									className={shouldShowShortcut ? styles.itemShortcut : styles.itemShortcutHidden}
									aria-hidden={shouldShowShortcut ? undefined : true}
								>
									{shortcut}
								</span>
							)}
						</>
					)}
				</div>
				{icon && !shouldShowShortcut && <div className={styles.itemIcon}>{icon}</div>}
			</AriaMenuItem>
		);
	},
);

MenuItem.displayName = 'MenuItem';
interface SubMenuProps {
	label: string;
	disabled?: boolean;
	hint?: string;
	danger?: boolean;
	children?: React.ReactNode;
	onTriggerSelect?: () => void;
}
export const SubMenu = React.forwardRef<HTMLDivElement, SubMenuProps>(
	({label, disabled, hint, danger = false, children, onTriggerSelect}, forwardedRef) => {
		const {t} = useLingui();
		const handleTriggerPress = useCallback(() => {
			if (disabled) return;
			onTriggerSelect?.();
		}, [disabled, onTriggerSelect]);
		const handleLabelClick = useCallback(
			(event: React.MouseEvent<HTMLDivElement>) => {
				if (disabled || !onTriggerSelect) return;
				const target = event.target as HTMLElement;
				if (target.closest('[data-submenu-caret="true"]')) {
					return;
				}
				event.preventDefault();
				event.stopPropagation();
				onTriggerSelect();
			},
			[disabled, onTriggerSelect],
		);
		const handleLabelKeyDown = useCallback(
			(event: React.KeyboardEvent<HTMLDivElement>) => {
				if (disabled || !onTriggerSelect) return;
				if (event.key === 'Enter' || event.key === ' ') {
					event.preventDefault();
					onTriggerSelect();
				}
			},
			[disabled, onTriggerSelect],
		);
		const handleMenuAction = useCallback((_key: React.Key) => {
			return false;
		}, []);
		return (
			<AriaSubmenuTrigger>
				<AriaMenuItem
					ref={forwardedRef}
					isDisabled={disabled}
					className={clsx(styles.item, {
						[styles.disabled]: disabled,
						[styles.danger]: danger,
					})}
					textValue={label}
					onAction={onTriggerSelect ? handleTriggerPress : undefined}
				>
					<div
						className={clsx(styles.itemLabelContainer)}
						onClick={handleLabelClick}
						onKeyDown={handleLabelKeyDown}
						role="button"
						tabIndex={-1}
					>
						<div className={styles.itemLabelText}>{label}</div>
						{hint && <div className={styles.itemHint}>{hint}</div>}
					</div>
					<svg
						className={styles.submenuCaret}
						width="16"
						height="16"
						viewBox="0 0 256 256"
						aria-hidden="true"
						data-submenu-caret="true"
					>
						<path
							fill="currentColor"
							d="M184.49 136.49l-80 80a12 12 0 0 1-17-17L159 128L87.51 56.49a12 12 0 1 1 17-17l80 80a12 12 0 0 1-.02 17"
						/>
					</svg>
				</AriaMenuItem>
				<AriaPopover
					placement="right top"
					offset={12}
					className={styles.submenuPopover}
					onMouseMoveCapture={stopHoverEventWhenWindowCannotUseHover}
					onMouseOverCapture={stopHoverEventWhenWindowCannotUseHover}
					onPointerMoveCapture={stopHoverEventWhenWindowCannotUseHover}
					onPointerOverCapture={stopHoverEventWhenWindowCannotUseHover}
				>
					<Scroller
						className={styles.submenuScroller}
						overflow="auto"
						fade={false}
						key={`context-submenu-scroller-${label}`}
					>
						<AriaMenu
							className={styles.ariaMenu}
							aria-label={t`Submenu`}
							autoFocus="first"
							selectionMode="none"
							onAction={handleMenuAction}
						>
							{children}
						</AriaMenu>
					</Scroller>
				</AriaPopover>
			</AriaSubmenuTrigger>
		);
	},
);

SubMenu.displayName = 'SubMenu';
export const MenuSeparator: React.FC = observer(() => {
	return <AriaSeparator className={styles.separator} />;
});
interface CheckboxItemProps {
	label?: string;
	checked: boolean;
	onCheckedChange: (checked: boolean) => void;
	disabled?: boolean;
	icon?: React.ReactNode;
	children?: React.ReactNode;
	danger?: boolean;
	closeOnChange?: boolean;
}
export const CheckboxItem = React.forwardRef<HTMLDivElement, CheckboxItemProps>(
	(
		{label, checked, onCheckedChange, disabled, icon: _icon, children, danger = false, closeOnChange = false},
		forwardedRef,
	) => {
		const closeMenu = useContext(ContextMenuCloseContext);
		const setItemRef = useCheckedMenuItemRef('menuitemcheckbox', checked, forwardedRef);
		const handleAction = useCallback(() => {
			if (disabled) return;
			onCheckedChange(!checked);
			if (closeOnChange) {
				closeMenu();
			}
		}, [checked, closeMenu, closeOnChange, disabled, onCheckedChange]);
		return (
			<AriaMenuItem
				ref={setItemRef}
				onAction={handleAction}
				isDisabled={disabled}
				shouldCloseOnSelect={closeOnChange}
				className={clsx(styles.item, styles.checkboxItem, {
					[styles.disabled]: disabled,
					[styles.danger]: danger,
				})}
				textValue={label || textValueFromNode(children)}
			>
				<div className={styles.itemLabel}>{children || label}</div>
				<div className={styles.checkboxIndicator} aria-hidden="true">
					<div
						className={clsx(styles.checkbox, {
							[styles.checkboxChecked]: checked,
						})}
					/>
				</div>
			</AriaMenuItem>
		);
	},
);

CheckboxItem.displayName = 'CheckboxItem';
interface MenuGroupProps {
	label?: string;
	children?: React.ReactNode;
}
export const MenuGroup: React.FC<MenuGroupProps> = observer(({children}) => {
	const validChildren = React.Children.toArray(children).filter((child): child is React.ReactElement => {
		if (!React.isValidElement(child)) return false;
		if (child.type === React.Fragment && !(child.props as {children?: React.ReactNode}).children) return false;
		return true;
	});
	if (validChildren.length === 0) {
		return null;
	}
	return <AriaMenuSection className={styles.group}>{validChildren}</AriaMenuSection>;
});
export const ContextMenu: React.FC = observer(() => {
	if (isMobileExperienceEnabled()) {
		return null;
	}
	const contextMenu = ContextMenuState.contextMenu;
	if (!contextMenu) return null;
	return createPortal(<RootContextMenu key={contextMenu.id} contextMenu={contextMenu} />, document.body);
});
